/**
 * Saved browser logins in the active organisation (AMO-152), for Settings →
 * Security: the person's own switch, the sites Amoreg keeps them signed into
 * across chats, and forgetting one or all of them.
 *
 * Hidden entirely when the workspace server does not run saved logins.
 *
 * @module components/settings/BrowserLoginsSection
 */
import { useEffect, useState } from "react";

import {
  fetchBrowserLogins,
  forgetBrowserLogins,
  updateBrowserLoginSettings,
  type BrowserLogins,
} from "../../state/browserLogins";
import { useActiveOrg } from "../../state/orgs";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export function BrowserLoginsSection() {
  const org = useActiveOrg();
  const organizationId = org?.id ?? null;
  const [loaded, setLoaded] = useState<BrowserLogins | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [confirmingForget, setConfirmingForget] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setLoaded(null);
    setLoadFailed(false);
    setConfirmingForget(false);
    setError(null);
    void fetchBrowserLogins(organizationId).then((result) => {
      if (cancelled) return;
      if (result === null) setLoadFailed(true);
      else setLoaded(result);
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, loadAttempt]);

  if (!org || !organizationId) return null;
  if (loaded && !loaded.enabled) return null;
  if (!loaded && !loadFailed) return null;
  if (loaded && !loaded.member) {
    return (
      <SettingsSection id="browser-logins" title="Saved browser logins">
        <SettingsRow
          id="browser-logins-not-member"
          title="Not a member here"
          description={`Amoreg keeps browser logins only in organisations you belong to. You can see ${org.name}, but you are not a member, so nothing is saved for you here.`}
        />
      </SettingsSection>
    );
  }

  const run = async (work: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    const failure = await work();
    setBusy(false);
    if (failure) setError(failure);
  };

  const setOn = (on: boolean) =>
    run(async () => {
      const failure = await updateBrowserLoginSettings(organizationId, { on });
      if (failure) return failure;
      // Switching off forgets everything on the server; mirror that here.
      setLoaded((current) =>
        current ? { ...current, on, sites: on ? current.sites : [] } : current,
      );
      return null;
    });

  const forgetSite = (site: string) =>
    run(async () => {
      const failure = await forgetBrowserLogins(organizationId, site);
      if (failure) return failure;
      setLoaded((current) =>
        current ? { ...current, sites: current.sites.filter((row) => row.site !== site) } : current,
      );
      return null;
    });

  const forgetAll = () =>
    run(async () => {
      const failure = await forgetBrowserLogins(organizationId);
      setConfirmingForget(false);
      if (failure) return failure;
      setLoaded((current) => (current ? { ...current, sites: [] } : current));
      return null;
    });

  const sites = loaded?.sites ?? [];
  const orgOff = loaded !== null && !loaded.orgOn;

  return (
    <SettingsSection
      id="browser-logins"
      title="Saved browser logins"
      headerAction={
        sites.length > 0 ? (
          <div className="flex items-center gap-2">
            {confirmingForget ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setConfirmingForget(false)}
              >
                Keep them
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant={confirmingForget ? "destructive" : "outline"}
              disabled={busy}
              onClick={() => (confirmingForget ? void forgetAll() : setConfirmingForget(true))}
            >
              {confirmingForget ? "Forget all" : "Forget all logins"}
            </Button>
          </div>
        ) : null
      }
    >
      {loadFailed ? (
        <p role="alert" className="px-3 py-2 text-sm text-destructive sm:px-4">
          Your saved logins could not be loaded.
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={() => setLoadAttempt((n) => n + 1)}
          >
            Try again
          </Button>
        </p>
      ) : (
        <>
          <SettingsRow
            {...searchableSetting("browser-logins")}
            description={
              orgOff
                ? `${org.name} has turned saved browser logins off for everyone. An organisation admin can turn them back on under Organization settings.`
                : `When you sign into a site in a chat's browser, Amoreg remembers it so your other chats in ${org.name} are already signed in. Only you can use these logins, and only in your private chats. Turning this off forgets every saved login.`
            }
            control={
              <Switch
                aria-label="Remember my browser logins"
                checked={!orgOff && (loaded?.on ?? false)}
                disabled={busy || !loaded || orgOff}
                onCheckedChange={(on) => void setOn(on)}
              />
            }
          />
          {loaded?.on && !orgOff && sites.length === 0 ? (
            <SettingsRow
              id="browser-logins-empty"
              title="No saved logins yet"
              description="Sites appear here after you sign into them in a chat's browser. A login unused for 30 days is forgotten."
            />
          ) : null}
          {sites.map((row) => (
            <SettingsRow
              key={row.site}
              id={`browser-login-${row.site}`}
              title={row.site}
              description={`Last used ${shortDate(row.lastUsedAt)}`}
              control={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void forgetSite(row.site)}
                >
                  Forget
                </Button>
              }
            />
          ))}
        </>
      )}
      {error ? (
        <p role="alert" className="px-3 pb-2 text-sm text-destructive sm:px-4">
          {error}
        </p>
      ) : null}
    </SettingsSection>
  );
}
